import { colorOf } from './palette'

import type { BoardResponse, ColumnInfo } from '../api/client'
import { labelTable } from './BoardCell'

/**
 * M10-5：导出 XLSX。
 *
 * 刻意采用**和我们自己的 monday 导入器兼容**的版式（这样导出再导入能还原）：
 *
 *   第 1 行：看板名（单格）
 *   第 2 行：空行
 *   每个分组：
 *     单格行 = 分组名（字体用分组色，白色更醒目）
 *     下一行 = 表头（A 列固定 "Name"，其余是列标题，灰底加粗）
 *     再往下 = 该分组的条目（A 列条目名）
 *
 * 颜色列（status / dropdown / color-picker）会写单元格**填充色**，
 * 值写标签文字 —— 与 monday 导出的形状一致，我们的导入器就能反解出 color 列。
 */

/** 把 "#00c875" / "00c875" 变成 exceljs 需要的 ARGB */
function toArgb(hex: string | undefined, fallback = 'FFE8EEF6'): string {
  if (hex === undefined || hex === '') return fallback
  const h = hex.replace(/^#/, '').toUpperCase()
  if (h.length === 6) return `FF${h}`
  if (h.length === 8) return h
  return fallback
}

/** 分组色名 → hex（与我们调色板一致；未知色用蓝） */
// 分组色统一从 palette 取（避免色名表漂移）
const GROUP_HEX: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_t, key: string) => colorOf(key).replace("#", ""),
})

interface CellLook {
  text: string
  fill?: string
  /** backlog #7：颜色列的原始标签索引，写成批注带回去（不改可见内容，不影响 monday） */
  note?: string
}

/** 把单元格的存储值变成"给人看的文字 + 可选的填充色" */
function renderCell(col: ColumnInfo, raw: string): CellLook {
  if (raw === '') return { text: '' }
  if (col.type === 'color' || col.type === 'dropdown' || col.type === 'color-picker') {
    const t = labelTable(col)
    const text = t.text[raw] ?? raw
    const hex = t.color[raw] ?? (raw.startsWith('#') ? raw : undefined)
    return {
      text,
      fill: hex === undefined ? undefined : toArgb(hex),
      // 只有"索引型"值才写批注（颜色列的值本来就是标签索引）
      note: /^\d+$/.test(raw) ? `friday:idx=${raw}` : undefined,
    }
  }
  if (col.type === 'tag') {
    try {
      const arr: unknown = JSON.parse(raw)
      if (Array.isArray(arr)) return { text: arr.map((x) => String(x)).join(', ') }
    } catch {
      /* 不是 JSON 就当普通文本 */
    }
    return { text: raw }
  }
  if (col.type === 'boolean') return { text: raw === '1' ? '✓' : '✗' }
  return { text: raw }
}

export async function boardToXlsxBlob(data: BoardResponse): Promise<Blob> {
  // 按需加载：exceljs 体积大（约 1MB），只在真正导入/导出 XLSX 时才拉进来
  const ExcelJS = (await import('exceljs')).default
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Friday'
  wb.created = new Date()

  // sheet 名不能超过 31 字符、不能含 : \ / ? * [ ]
  const sheetName = (data.board.name || 'Board').replace(/[:\\/?*[\]]/g, '_').slice(0, 31)
  const ws = wb.addWorksheet(sheetName)

  // name 类型的列由 A 列（条目名）代表，不再重复导出一份
  const columns = data.columns.filter((c) => c.hidden !== true && c.type !== 'name')
  const headers = ['Name', ...columns.map((c) => c.title)]

  // 列宽：Name 宽一点，其余按列宽折算
  ws.columns = headers.map((_h, i) => ({
    width: i === 0 ? 28 : Math.max(12, Math.min(40, Math.round((columns[i - 1]?.width ?? 120) / 8))),
  }))

  let row = 1
  /** 表头灰底 */
  const styleHeader = (r: number) => {
    for (let c = 1; c <= headers.length; c += 1) {
      const cell = ws.getCell(r, c)
      cell.font = { bold: true, size: 11 }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6D6D6' } }
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } } }
    }
  }

  // 第 1 行：看板名
  ws.getCell(1, 1).value = data.board.name
  ws.getCell(1, 1).font = { bold: true, size: 14 }
  row = 3

  for (const g of data.groups) {
    // 分组名：单格行
    const gCell = ws.getCell(row, 1)
    gCell.value = g.title
    // 分组色写**字体颜色**（与 monday 导出一致，我们的导入器读的也是字体色）
    gCell.font = { bold: true, size: 12, color: { argb: toArgb(GROUP_HEX[g.color] ?? '579BFC') } }
    row += 1

    styleHeader(row)
    headers.forEach((h, i) => {
      ws.getCell(row, i + 1).value = h
    })
    row += 1

    const items = data.items.filter((it) => it.groupId === g.id)
    for (const it of items) {
      ws.getCell(row, 1).value = it.name
      columns.forEach((c, i) => {
        const raw = it.values[c.key] ?? ''
        const look = renderCell(c, raw)
        const cell = ws.getCell(row, i + 2)
        if (c.type === 'numeric' && raw !== '' && !Number.isNaN(Number(raw))) cell.value = Number(raw)
        else cell.value = look.text
        if (look.note !== undefined) cell.note = look.note
        if (look.fill !== undefined) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: look.fill } }
          // 深色底配白字，浅色底配黑字
          const v = parseInt(look.fill.slice(2, 4), 16)
          cell.font = { color: { argb: v > 160 ? 'FF333333' : 'FFFFFFFF' } }
        }
      })
      row += 1
    }
    row += 1 // 组间空行
  }

  // 隐藏表 friday-meta：记录每列的类型/宽度/标签/设置 —— 用来区分「我们自己的导出」并精确还原
  {
    const meta = wb.addWorksheet('friday-meta')
    meta.state = 'hidden'
    meta.getCell(1, 1).value = JSON.stringify({
      format: 'friday-board-xlsx',
      version: 1,
      // 与表头保持一致：name 类型列由 A 列代表，不进 meta，否则导入时列会整体错位一列
      // name 列也写进 meta（带 isName），导入时才知道名称列的自定义标题；表格里不重复写它
      columns: data.columns.filter((c) => c.hidden !== true).map((c) => ({
        isName: c.type === 'name',
        key: c.key,
        title: c.title,
        type: c.type,
        width: c.width,
        labels: c.labels,
        settings: c.settings,
      })),
    })
  }

  const buf = await wb.xlsx.writeBuffer()
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

/** 触发下载 */
export function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
